/**
 * Carbon buttons (DESIGN.md "Buttons"): square, flat, label left and a
 * trailing 16px icon right. lg 48px with a 200px minimum (the default), md
 * 40px, sm 32px. One primary (Soft Green, near-black text) per view; tertiary
 * (1px ink outline) beside it; secondary (gray) inside expanded rows; ghost
 * for link-weight actions. `ButtonLink` is the same look as a router link
 * (`to`), or with `href` as a plain link to an absolute URL in a new tab.
 */
import { forwardRef, type AnchorHTMLAttributes, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { Link, type LinkProps } from 'react-router-dom'
import clsx from 'clsx'
import { Spinner } from './Spinner'
import type { ButtonKind, ButtonSize } from './types'

interface ButtonLookProps {
  kind?: ButtonKind
  size?: ButtonSize
  /** Trailing icon, sized to 16px. */
  icon?: ReactNode
  /** Stretch to the container (a phone action row, a button set). */
  fullWidth?: boolean
  /** Long labels wrap onto more lines with the icon held top-right (Carbon). `false` keeps one line, ellipsised, with the label as the title. */
  wrap?: boolean
}

const BASE =
  'c-focus-inset inline-flex max-w-[20rem] items-start justify-between border ' +
  'text-left text-[14px] font-normal leading-[1.29] tracking-[0.16px] transition-colors duration-70 ease-carbon'

const SIZE: Record<ButtonSize, string> = {
  lg: 'min-h-12 gap-8 px-4 py-[15px]',
  md: 'min-h-10 gap-6 px-4 py-[11px]',
  sm: 'min-h-8 gap-4 px-3 py-[7px]',
}

const KIND: Record<ButtonKind, string> = {
  // Each kind sets its own border color: Tailwind emits border-transparent after the token colors, so a shared one would win.
  primary: 'border-transparent bg-interactive text-interactive-on hover:bg-interactive-hover active:bg-interactive-active',
  secondary:
    'border-transparent bg-[color:var(--button-secondary)] text-[color:var(--text-on-color)] hover:bg-[color:var(--button-secondary-hover)]',
  tertiary:
    'border-text-primary bg-transparent text-text-primary hover:bg-text-primary hover:text-[color:var(--background)]',
  ghost: 'border-transparent bg-transparent text-link hover:bg-layer-hover hover:text-link-hover',
}

const DISABLED: Record<ButtonKind, string> = {
  primary: 'disabled:cursor-not-allowed disabled:border-transparent disabled:bg-layer-selected disabled:text-text-helper',
  secondary: 'disabled:cursor-not-allowed disabled:border-transparent disabled:bg-layer-selected disabled:text-text-helper',
  tertiary: 'disabled:cursor-not-allowed disabled:border-transparent disabled:bg-layer-selected disabled:text-text-helper',
  ghost: 'disabled:cursor-not-allowed disabled:bg-transparent disabled:text-text-helper',
}

function look({ kind = 'primary', size = 'lg', fullWidth, wrap = true }: ButtonLookProps, className?: string) {
  return clsx(
    BASE,
    !wrap && 'whitespace-nowrap',
    SIZE[size],
    KIND[kind],
    DISABLED[kind],
    // Carbon's 200px minimum applies to full-size filled and outlined buttons only.
    size === 'lg' && kind !== 'ghost' && !fullWidth && 'min-w-[200px]',
    fullWidth && 'w-full max-w-none',
    className,
  )
}

const labelClass = (wrap = true) => (wrap ? 'min-w-0' : 'min-w-0 truncate')
const labelTitle = (wrap = true, children?: ReactNode) => (!wrap && typeof children === 'string' ? children : undefined)

const trailing = (node: ReactNode) =>
  node ? (
    <span aria-hidden="true" className="grid h-[18px] flex-none place-items-center [&>svg]:h-4 [&>svg]:w-4">
      {node}
    </span>
  ) : null

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'>, ButtonLookProps {
  /** Busy: the trailing icon becomes a spinner, the button is disabled and aria-busy. */
  loading?: boolean
  children: ReactNode
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { kind, size, icon, fullWidth, wrap, loading = false, disabled, className, children, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={look({ kind, size, fullWidth, wrap }, className)}
      title={labelTitle(wrap, children)}
      {...rest}
    >
      <span className={labelClass(wrap)}>{children}</span>
      {loading ? <Spinner size={16} /> : trailing(icon)}
    </button>
  )
})

export interface ButtonLinkProps extends Omit<LinkProps, 'children'>, ButtonLookProps {
  children: ReactNode
}

/**
 * An absolute URL outside Guardian (an admin center): a plain `<a>` that opens
 * a new tab, with `rel="noopener noreferrer"`, and says so to screen readers.
 */
export interface ButtonExternalLinkProps
  extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'children' | 'href' | 'target' | 'rel'>,
    ButtonLookProps {
  href: string
  to?: never
  children: ReactNode
}

export const ButtonLink = forwardRef<HTMLAnchorElement, ButtonLinkProps | ButtonExternalLinkProps>(function ButtonLink(props, ref) {
  if ('href' in props && typeof props.href === 'string') {
    const { kind, size, icon, fullWidth, wrap, className, children, href, ...rest } = props
    return (
      <a ref={ref} href={href} target="_blank" rel="noopener noreferrer" className={look({ kind, size, fullWidth, wrap }, className)} title={labelTitle(wrap, children)} {...rest}>
        <span className={labelClass(wrap)}>
          {children}
          <span className="sr-only"> (opens in a new tab)</span>
        </span>
        {trailing(icon)}
      </a>
    )
  }
  const { kind, size, icon, fullWidth, wrap, className, children, ...rest } = props as ButtonLinkProps
  return (
    <Link ref={ref} className={look({ kind, size, fullWidth, wrap }, className)} title={labelTitle(wrap, children)} {...rest}>
      <span className={labelClass(wrap)}>{children}</span>
      {trailing(icon)}
    </Link>
  )
})

export default Button
